import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { FileUpload } from "./FileUpload";
import { useState, useEffect } from "react";

const testimonialSchema = z.object({
  client_name: z.string().min(1, "Client name is required"),
  company: z.string().min(1, "Company is required"),
  role: z.string().optional(),
  quote_dk: z.string().min(1, "Danish quote is required"),
  quote_en: z.string().min(1, "English quote is required"),
  rating: z.number().min(1).max(5).optional(),
  photo: z.string().optional(),
  published: z.boolean().default(false),
});

type TestimonialFormValues = z.infer<typeof testimonialSchema>;

interface TestimonialFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: TestimonialFormValues) => void;
  initialData?: any;
  isLoading?: boolean;
}

export const TestimonialForm = ({ open, onOpenChange, onSubmit, initialData, isLoading }: TestimonialFormProps) => {
  const [photo, setPhoto] = useState<string | undefined>(initialData?.photo);

  const form = useForm<TestimonialFormValues>({
    resolver: zodResolver(testimonialSchema),
    defaultValues: initialData || {
      client_name: "",
      company: "",
      role: "",
      quote_dk: "",
      quote_en: "",
      rating: 5,
      photo: "",
      published: false,
    },
  });

  // Update form values when initialData changes (fixes issue when opening unpublished testimonials)
  useEffect(() => {
    if (initialData) {
      form.reset({
        client_name: initialData.client_name || "",
        company: initialData.company || "",
        role: initialData.role || "",
        quote_dk: initialData.quote_dk || "",
        quote_en: initialData.quote_en || "",
        rating: initialData.rating || 5,
        photo: initialData.photo || "",
        published: initialData.published || false,
      });
      setPhoto(initialData.photo);
    }
  }, [initialData, form]);

  const handleFormSubmit = (data: TestimonialFormValues) => {
    onSubmit({
      ...data,
      photo,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initialData ? "Edit Testimonial" : "Add Testimonial"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="client_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Client Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="company"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Role / Position</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="quote_dk"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Quote (Danish)</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={4} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="quote_en"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Quote (English)</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={4} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="rating"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rating (1-5)</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        min="1" 
                        max="5" 
                        {...field} 
                        onChange={e => field.onChange(parseInt(e.target.value))} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="col-span-2">
                <FileUpload
                  label="Client Photo"
                  bucket="testimonials"
                  accept="image/*"
                  maxSizeMB={2}
                  currentFile={photo}
                  onFileUploaded={(url) => setPhoto(url)}
                  onFileRemoved={() => setPhoto(undefined)}
                />
              </div>
            </div>

            <FormField
              control={form.control}
              name="published"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between">
                  <FormLabel>Published</FormLabel>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Saving..." : initialData ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
